import { Box, Chip, Paper, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { UnitDashboardTableRow } from "../../../../types";

const cardStyles = {
  boxShadow: "0px 1px 14px 0px #00071624",
  display: "flex",
  paddingX: "16px",
  paddingY: "16px",
  width: "100%",
  maxWidth: "100%", // Ensures it stays within parent container
  flexDirection: "column",
  overflow: "hidden", // Prevents content from overflowing outside the box
};

export default function BinData({ unit }: any) {
  const unitData = useSelector((state: RootState) => state.unitDirectory.unitData);

  const rows: UnitDashboardTableRow[] = [
    {
      id: unitData.unit_id,
      hvac_sn: unitData.bin_data_last_15min.hvac_sn,
      ENERGY_USAGE_KWH: Number(unitData.bin_data_last_15min.ENERGY_USAGE_KWH || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      ENERGY_SAVINGS_KWH: unitData.bin_data_last_15min.ENERGY_SAVINGS_KWH || 0,
      CARBON_REDUCTION_SAVINGS: unitData.bin_data_last_15min.CARBON_REDUCTION_SAVINGS || 0,
      SENSE1_TEMP_AVG_DEGF: unitData.bin_data_last_15min.SENSOR1_TEMP_AVG_F || 0,
      SENSE1_TEMP_MIN_DEGF: unitData.bin_data_last_15min.SENSOR1_TEMP_MIN_F || 0,
      SENSE1_TEMP_MAX_DEGF: unitData.bin_data_last_15min.SENSOR1_TEMP_MAX_F || 0,
      SENSE2_TEMP_AVG_DEGF: unitData.bin_data_last_15min.SENSOR2_TEMP_AVG_F || 0,
      SENSE2_TEMP_MIN_DEGF: unitData.bin_data_last_15min.SENSOR2_TEMP_MIN_F || 0,
      SENSE2_TEMP_MAX_DEGF: unitData.bin_data_last_15min.SENSOR2_TEMP_MAX_F || 0,
      SENSE3_TEMP_AVG_DEGF: unitData.bin_data_last_15min.SENSOR3_TEMP_AVG_F || 0,
      SENSE3_TEMP_MIN_DEGF: unitData.bin_data_last_15min.SENSOR3_TEMP_MIN_F || 0,
      SENSE3_TEMP_MAX_DEGF: unitData.bin_data_last_15min.SENSOR3_TEMP_MAX_F || 0,
      kw_tonnage: Number(unitData.bin_data_last_15min["kw/tonnage"] || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      running_status: unitData.bin_data_last_15min.running_status === null ? "OFFLINE" : unitData.bin_data_last_15min.running_status === true ? "ONLINE" : "BYPASS",
      timestamp: unitData.bin_data_last_15min.timestamp || "-",
      pace_ai_version: unitData.pace_ai_version || "-",
      firmware_version: unitData.firmware_version || "-",
      electric_demand_last_15min: unitData.electric_demand_last_15min || 0,
      pace_device_serial_number: unitData.pace_device_serial_number || "-",
    },
  ];

  const columns: GridColDef<UnitDashboardTableRow>[] = [
    {
      field: "timestamp",
      headerName: "Timestamp",
      minWidth: 250,
    },
    {
      field: "hvac_sn",
      headerName: "HVAC SN",
      minWidth: 140,
    },
    {
      field: "pace_device_serial_number",
      headerName: "PACE AI Device ID",
      minWidth: 190,
    },
    {
      field: "pace_ai_version",
      headerName: "PACE AI VERSION",
      minWidth: 150,
    },
    {
      field: "firmware_version",
      headerName: "Firmware",
      minWidth: 120,
    },
    // {
    //   field: "ENERGY_SAVINGS_KWH",
    //   headerName: "Energy Savings (kWh)",
    //   minWidth: 160,
    // },
    {
      field: "ENERGY_USAGE_KWH",
      headerName: "Energy Usage (kWh)",
      minWidth: 150,
    },
    // {
    //   field: "CARBON_REDUCTION_SAVINGS",
    //   headerName: "Carbon Reduction Savings",
    //   minWidth: 200,
    // },
    {
      field: "SENSE1_TEMP_AVG_DEGF",
      headerName: "Sense 1 Avg (F)",
      minWidth: 150,
    },
    {
      field: "SENSE1_TEMP_MIN_DEGF",
      headerName: "Sense 1 Min (F)",
      minWidth: 150,
    },
    {
      field: "SENSE1_TEMP_MAX_DEGF",
      headerName: "Sense 1 Max (F)",
      minWidth: 150,
    },
    {
      field: "SENSE2_TEMP_AVG_DEGF",
      headerName: "Sense 2 Avg (F)",
      minWidth: 150,
    },
    {
      field: "SENSE2_TEMP_MIN_DEGF",
      headerName: "Sense 2 Min (F)",
      minWidth: 150,
    },
    {
      field: "SENSE2_TEMP_MAX_DEGF",
      headerName: "Sense 2 Max (F)",
      minWidth: 150,
    },
    {
      field: "SENSE3_TEMP_AVG_DEGF",
      headerName: "Sense 3 Avg (F)",
      minWidth: 150,
    },
    {
      field: "SENSE3_TEMP_MIN_DEGF",
      headerName: "Sense 3 Min (F)",
      minWidth: 150,
    },
    {
      field: "SENSE3_TEMP_MAX_DEGF",
      headerName: "Sense 3 Max (F)",
      minWidth: 150,
    },
    {
      field: "kw_tonnage",
      headerName: "kW/Tonnage",
      minWidth: 150,
    },
    {
      field: "running_status",
      headerName: "Running Status",
      minWidth: 150,
      renderCell: (params) => <Chip label={params.value} variant="filled" color={params.value === "ONLINE" ? "success" : params.value === "OFFLINE" ? "default" : "info"} />,
    },
  ];

  return (
    <Box>
      <Paper
        sx={{
          ...cardStyles,
          overflowX: "auto", // Allow horizontal scrolling
        }}
      >
        <Typography variant="h5">Bin data last 15 min</Typography>
        <Box sx={{ marginTop: "20px" }} />
        <Box sx={{ width: "100%" }}>
          {" "}
          {/* Wrapper Box for horizontal scrolling */}
          <DataGrid
            sx={{
              width: "100%",
              "& .MuiDataGrid-cell": {
                border: 1,
                borderRight: 0,
                borderTop: 1,
                borderLeft: 0,
                borderColor: "#E2E9F4",
                whiteSpace: "nowrap", // Prevents text wrapping
              },
              "& .MuiDataGrid-footerContainer": {
                display: "none",
              },
            }}
            autoHeight // Ensure vertical expansion
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            hideFooterPagination
            disableColumnFilter
            disableColumnMenu
          />
        </Box>
      </Paper>
    </Box>
  );
}
